import React, { useState, useEffect, useRef } from 'react';
import { Share2, Copy, Check, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

/**
 * ShareButton — universal product sharing component.
 *
 * - Mobile / supported browsers: opens native Web Share API
 * - Desktop / fallback: opens a dropdown with WhatsApp / Facebook / Twitter /
 *   Telegram / LinkedIn / Email / Copy link
 *
 * Uses `/api/og/products/{slug}` for social share targets so the bot scraper
 * sees rich Open Graph preview (image + title + price + availability).
 * Email and "Copy link" use the canonical /products/{slug} URL for cleanliness.
 */
const ShareButton = ({ product, productName, productPrice }) => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  const t = (it, en, es) => (language === 'it' ? it : language === 'es' ? es : en);

  const slug = product.slug || product.sku;
  const canonicalUrl = `https://allmx.it/products/${slug}`;
  // OG-prerender URL: bots see rich preview, humans get 302 → canonical
  const shareUrl = `https://allmx.it/api/og/products/${slug}`;

  const shareText = productPrice
    ? `${productName} — €${productPrice.toFixed(2)} | ALLMX`
    : `${productName} | ALLMX`;
  const emailSubject = t(
    `Guarda questo prodotto su ALLMX: ${productName}`,
    `Check out this ALLMX product: ${productName}`,
    `Mira este producto de ALLMX: ${productName}`
  );
  const emailBody = t(
    `Ho trovato questo prodotto su ALLMX e ho pensato potesse interessarti:\n\n${productName}\nPrezzo: €${productPrice ? productPrice.toFixed(2) : ''}\n\n${canonicalUrl}\n`,
    `I found this product on ALLMX, thought you'd like it:\n\n${productName}\nPrice: €${productPrice ? productPrice.toFixed(2) : ''}\n\n${canonicalUrl}\n`,
    `Encontré este producto en ALLMX y pensé que te gustaría:\n\n${productName}\nPrecio: €${productPrice ? productPrice.toFixed(2) : ''}\n\n${canonicalUrl}\n`
  );

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: productName,
        text: shareText,
        url: shareUrl,
      });
    } catch (err) {
      // User dismissed the share sheet — silent
    }
  };

  const handleClick = () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      handleNativeShare();
    } else {
      setOpen(!open);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      toast.success(t('Link copiato!', 'Link copied!', '¡Enlace copiado!'));
      setTimeout(() => setCopied(false), 2000);
      setOpen(false);
    } catch {
      toast.error(t('Impossibile copiare', 'Cannot copy', 'No se puede copiar'));
    }
  };

  const openSocial = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
    setOpen(false);
  };

  const socials = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      bg: '#25D366',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26L3.388 21l4.266-1.193zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
        </svg>
      ),
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      bg: '#1877F2',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      bg: '#000000',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: 'telegram',
      label: 'Telegram',
      bg: '#26A5E4',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      bg: '#0A66C2',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={handleClick}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors hover:bg-[var(--brand-primary)] hover:text-black hover:border-[var(--brand-primary)]"
        style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        data-testid="share-button"
        aria-label={t('Condividi', 'Share', 'Compartir')}
      >
        <Share2 size={16} />
        <span>{t('Condividi', 'Share', 'Compartir')}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 z-50 shadow-2xl border"
          style={{
            background: 'var(--bg-secondary, #1a1a1a)',
            borderColor: 'var(--border-primary)',
          }}
          data-testid="share-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-primary)' }}>
            <span className="text-sm font-semibold">
              {t('Condividi prodotto', 'Share product', 'Compartir producto')}
            </span>
            <button
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="py-2">
            {socials.map((s) => (
              <button
                key={s.key}
                onClick={() => openSocial(s.url)}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors text-left"
                data-testid={`share-${s.key}`}
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: s.bg }}
                >
                  {s.icon}
                </span>
                <span style={{ color: 'var(--text-primary)' }}>{s.label}</span>
              </button>
            ))}

            <button
              onClick={() => {
                window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors text-left"
              data-testid="share-email"
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-600">
                <Mail size={16} color="white" />
              </span>
              <span style={{ color: 'var(--text-primary)' }}>Email</span>
            </button>

            <div className="border-t my-1" style={{ borderColor: 'var(--border-primary)' }} />

            <button
              onClick={handleCopy}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors text-left"
              data-testid="share-copy"
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[var(--brand-primary)]">
                {copied ? <Check size={16} color="black" /> : <Copy size={16} color="black" />}
              </span>
              <span style={{ color: 'var(--text-primary)' }}>
                {copied ? t('Copiato!', 'Copied!', '¡Copiado!') : t('Copia link', 'Copy link', 'Copiar enlace')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
